import React from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Progress,
  SimpleGrid,
  Text,
} from "@chakra-ui/react";
import { DateTime } from "luxon";
import { HolidayItem } from "./HolidayCalendarModal";

export interface LeavePolicyType {
  code: string;
  name: string;
  annualQuota: number;
  isPaid?: boolean;
  isUnlimited?: boolean;
  carryForwardAllowed?: boolean;
  maxCarryForwardDays?: number;
  advanceNoticeDays?: number;
}

export interface LeavePolicyBalance {
  leaveCycleStartDate?: string | null;
  cycleStart?: string | null;
  cycleEnd?: string | null;
  balances?: Record<string, { annual: number; used: number; remaining: number }>;
  eligibility?: Record<string, { eligible: boolean; reason: string }>;
}

interface LeavePolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
  leaveTypes: LeavePolicyType[];
  /** The signed-in employee's balance, to show their own usage per type. */
  balance?: LeavePolicyBalance | null;
  holidays: HolidayItem[];
  onViewHolidays?: () => void;
}

const TYPE_ORDER = ["casual", "sick", "exigency", "optional", "compensatory", "unpaid"];
const TYPE_COLOR: Record<string, string> = {
  casual: "teal",
  sick: "red",
  exigency: "orange",
  optional: "purple",
  compensatory: "blue",
  unpaid: "gray",
};

const formatDate = (iso?: string | null) =>
  iso ? DateTime.fromISO(iso).toFormat("dd LLL yyyy") : "";

/**
 * Leave Policy at a glance: one card per active leave type (from Leave Policy
 * Master) with the employee's own usage, plus a summary of this year's
 * holidays. Matches the Holiday Calendar's look.
 */
export const LeavePolicyModal = ({
  isOpen,
  onClose,
  leaveTypes,
  balance,
  holidays,
  onViewHolidays,
}: LeavePolicyModalProps) => {
  const year = DateTime.now().year;
  const yearHolidays = holidays.filter((h) => h.date.startsWith(`${year}-`));
  const countOf = (type: string) =>
    yearHolidays.filter((h) => (h.type || "national") === type).length;

  const types = [...leaveTypes].sort((a, b) => {
    const ai = TYPE_ORDER.indexOf(a.code);
    const bi = TYPE_ORDER.indexOf(b.code);
    return (ai === -1 ? TYPE_ORDER.length : ai) - (bi === -1 ? TYPE_ORDER.length : bi);
  });
  const optionalQuota = leaveTypes.find((t) => t.code === "optional")?.annualQuota;

  // Leave years run from each employee's own cycle start, not a fixed month.
  const leaveYear =
    balance?.cycleStart && balance?.cycleEnd
      ? `Your leave year: ${formatDate(balance.cycleStart)} – ${formatDate(balance.cycleEnd)}`
      : balance?.leaveCycleStartDate
        ? `Your leave year starts ${formatDate(balance.leaveCycleStartDate)}`
        : "";

  const holidayStats = [
    { label: "National holidays", value: countOf("national"), color: "green" },
    {
      label: optionalQuota ? `Optional holidays (pick ${optionalQuota})` : "Optional holidays",
      value: countOf("optional"),
      color: "purple",
    },
    { label: "2nd & 3rd Saturdays off", value: countOf("weekly_off"), color: "blue" },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="4xl"
      isCentered
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent maxH="88vh" borderRadius="14px">
        <ModalHeader pb={2}>
          <Text fontSize="20px" fontWeight="700" m={0}>
            Leave Policy
          </Text>
          {leaveYear && (
            <Text fontSize="13px" fontWeight="500" color="gray.500" m={0} mt={1}>
              {leaveYear}
            </Text>
          )}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody pb={6}>
          {types.length === 0 ? (
            <Text color="gray.500" fontSize="sm" py={8} textAlign="center">
              No leave types configured yet.
            </Text>
          ) : (
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={3}>
              {types.map((type) => {
                const color = TYPE_COLOR[type.code] || "gray";
                const own = balance?.balances?.[type.code];
                const eligibility = balance?.eligibility?.[type.code];
                const unlimited = type.isUnlimited || own?.annual === -1;
                // Optional leave can be fewer than the master quota (one for
                // after-June joiners), so show the employee's own figure.
                const quota =
                  type.code === "optional" && own ? own.annual : type.annualQuota;
                const notes = [
                  type.code === "optional" ? "Calendar year: renews every 1 Jan" : null,
                  type.advanceNoticeDays
                    ? `Apply ${type.advanceNoticeDays} day${type.advanceNoticeDays === 1 ? "" : "s"} in advance`
                    : null,
                  type.carryForwardAllowed
                    ? `Carry forward up to ${type.maxCarryForwardDays || 0} days`
                    : unlimited
                      ? null
                      : "No carry forward",
                ].filter(Boolean);

                return (
                  <Box
                    key={type.code}
                    border="1px solid #e2e8f0"
                    borderTop="3px solid"
                    borderTopColor={`${color}.400`}
                    borderRadius="12px"
                    p={3}
                    bg="white"
                  >
                    <Flex justify="space-between" align="center" gap={2}>
                      <Text fontSize="14px" fontWeight="700" m={0} noOfLines={1}>
                        {type.name}
                      </Text>
                      <Badge
                        colorScheme={type.isPaid === false ? "gray" : "green"}
                        borderRadius="full"
                        px={2}
                        fontSize="9px"
                      >
                        {type.isPaid === false ? "Unpaid" : "Paid"}
                      </Badge>
                    </Flex>

                    <Flex align="baseline" gap={1.5} mt={1}>
                      <Text fontSize="26px" fontWeight="800" color={`${color}.600`} m={0}>
                        {unlimited ? "∞" : quota}
                      </Text>
                      <Text fontSize="12px" color="gray.500" m={0}>
                        {unlimited ? "no annual limit" : "days / year"}
                      </Text>
                    </Flex>

                    {eligibility && !eligibility.eligible ? (
                      <Text fontSize="12px" color="gray.500" m={0} mt={1}>
                        Not eligible yet: {eligibility.reason}
                      </Text>
                    ) : own ? (
                      <Box mt={1}>
                        {!unlimited && (
                          <Progress
                            value={own.annual > 0 ? (own.used / own.annual) * 100 : 0}
                            size="xs"
                            borderRadius="full"
                            colorScheme={color}
                            mb={1}
                          />
                        )}
                        <Text fontSize="12px" color="gray.600" m={0}>
                          {unlimited
                            ? `You've used ${own.used}`
                            : `You've used ${own.used} of ${own.annual} · ${own.remaining} left`}
                        </Text>
                      </Box>
                    ) : null}

                    {notes.length > 0 && (
                      <Text fontSize="11px" color="gray.500" m={0} mt={2}>
                        {notes.join(" · ")}
                      </Text>
                    )}
                  </Box>
                );
              })}
            </SimpleGrid>
          )}

          <Box
            mt={4}
            borderRadius="12px"
            bg="#e8f3f3"
            px={4}
            py={3}
          >
            <Flex
              justify="space-between"
              align={{ base: "flex-start", md: "center" }}
              direction={{ base: "column", md: "row" }}
              gap={3}
            >
              <Box>
                <Text fontSize="13px" fontWeight="700" color="#327171" m={0}>
                  Holidays {year}
                </Text>
                <Flex gap={{ base: 3, md: 6 }} mt={1} wrap="wrap">
                  {holidayStats.map((stat) => (
                    <Flex key={stat.label} align="center" gap={2}>
                      <Box w="8px" h="8px" borderRadius="full" bg={`${stat.color}.400`} />
                      <Text fontSize="18px" fontWeight="800" m={0}>
                        {stat.value}
                      </Text>
                      <Text fontSize="12px" color="gray.600" m={0}>
                        {stat.label}
                      </Text>
                    </Flex>
                  ))}
                </Flex>
              </Box>
              {onViewHolidays && (
                <Button
                  size="sm"
                  bg="#327171"
                  color="white"
                  _hover={{ bg: "#285e5e" }}
                  flexShrink={0}
                  onClick={onViewHolidays}
                >
                  View Holiday Calendar
                </Button>
              )}
            </Flex>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
